import { type FC, Suspense, useMemo, useState } from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";
import {
  Decimal,
  Fiat,
  IconDenom,
  Icons,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TablePagination,
  TableTooltipLabel,
  TranslationProvider,
  applySortDir,
  compareBigInts,
  compareNullableBigInts,
  compareNumbers,
  classApr,
  formatApr,
  nFormatter,
  useTablePagination,
  useTableSort,
  useLocale,
  useTranslation,
} from "rujira.ui";
import { usePreloadedBalances } from "../common/components/Balance";
import { Graph } from "../merge/components/Graph";
import { getStaticProductSeo, ProductSeoHelmet } from "../seo";
import { usePreloadedAccountData } from "../services/accountData";
import { Subscription } from "../services/useNodeSubscription";
import type {
  StakeOverviewAccountFragment$data,
  StakeOverviewAccountFragment$key,
} from "./__generated__/StakeOverviewAccountFragment.graphql";
import type {
  StakeOverviewQuery,
  StakeOverviewQuery$data,
} from "./__generated__/StakeOverviewQuery.graphql";
import { stakeRoute } from "./routes";

const EXCLUDED_STAKING_SYMBOLS = new Set(["NAMI"]);
const STAKE_SYMBOL_ORDER = new Map([
  ["RUJI", 0],
  ["BRUNE", 1],
  ["TCY", 2],
]);
const STAKE_OVERVIEW_LOADING_ROWS = [
  "stake-overview-loading-1",
  "stake-overview-loading-2",
  "stake-overview-loading-3",
];
const STAKING_ANALYTICS_PERIOD_DAYS = 30;
const STAKING_ANALYTICS_LOOKBACK_DAYS = 7;
const DAYS_PER_YEAR = 365n;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const query = graphql`
  query StakeOverviewQuery(
    $analyticsFrom: Timestamp!
    $analyticsTo: Timestamp!
    $analyticsPeriod: Int!
  ) {
    staking {
      pools {
        id
        bondAsset {
          metadata {
            symbol
            decimals
          }
          price {
            current
          }
        }
        stakingStatus: status {
          accountBond
          liquidBondSize
          valueUsd
        }
        stakingSummary: summary {
          apr {
            value
            status
          }
        }
        analyticsBins(
          first: 7
          from: $analyticsFrom
          to: $analyticsTo
          resolution: "1D"
          period: $analyticsPeriod
        ) {
          edges {
            node {
              bin
              totalRevenue {
                value
                movingAvg
              }
              totalBalanceStaked {
                value
                movingAvg
              }
            }
          }
        }
      }
    }
  }
`;

const accountFragment = graphql`
  fragment StakeOverviewAccountFragment on Account {
    stakingV2 {
      id
      pool {
        id
        bondAsset {
          metadata {
            symbol
          }
        }
      }
      bonded {
        amount
      }
      liquidSize {
        amount
      }
      valueUsd
    }
  }
`;

const subscription = graphql`
  subscription StakeOverviewPoolSubscription($id: ID!) {
    node(id: $id) {
      ... on StakingPool {
        status {
          accountBond
          liquidBondSize
          valueUsd
        }
        stakingSummary: summary {
          apr {
            value
            status
          }
        }
      }
    }
  }
`;

type StakeOverviewPool = StakeOverviewQuery$data["staking"]["pools"][number];
type StakeOverviewStakingAccount =
  StakeOverviewAccountFragment$data["stakingV2"][number];
type StakeOverviewAnalyticsEdge = NonNullable<
  NonNullable<StakeOverviewPool["analyticsBins"]>["edges"]
>[number];
type StakeOverviewAnalyticsBin = NonNullable<
  NonNullable<StakeOverviewAnalyticsEdge>["node"]
>;
const STAKE_OVERVIEW_SORT_KEYS = [
  "token",
  "tvl",
  "apr",
  "yearlyRewards",
  "autoCompounding",
  "myBalance",
] as const;
type StakeOverviewSortBy = (typeof STAKE_OVERVIEW_SORT_KEYS)[number];
type StakeOverviewAccountByPoolId = ReadonlyMap<
  string,
  StakeOverviewStakingAccount
>;

const STAKE_OVERVIEW_COLUMNS = [
  { id: "logo", width: "2.5rem" },
  { id: "token", width: "calc(15% - 2.5rem)", minWidth: "8rem" },
  { id: "tvl", width: "13%", minWidth: "8rem" },
  { id: "autoCompounding", width: "18%", minWidth: "9rem" },
  { id: "yearlyRewards", width: "18%", minWidth: "10rem" },
  { id: "apr", width: "14%", minWidth: "6rem" },
  { id: "balance", width: "11%", minWidth: "7rem" },
  { id: "action", width: "10%", minWidth: "6rem" },
] as const;

const EMPTY_STAKING_ACCOUNTS: readonly StakeOverviewStakingAccount[] = [];

export const StakeOverview: FC = () => {
  const { locale } = useLocale();

  return (
    <TranslationProvider namespace="staking">
      <ProductSeoHelmet seo={getStaticProductSeo("/stake", locale)} />
      <StakeOverviewContent />
    </TranslationProvider>
  );
};

const StakeOverviewContent: FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <main className="rujira__main rujira__main--gradient stake-overview">
        <div className="rujira__inner--pad">
          <div className="rujira__inner">
            <section className="stake-overview__hero">
              <h1 className="h1">{t("title")}</h1>
              <p className="fs-20 lh-28 mw-md-100 balance">
                {t("description")}
              </p>
            </section>
            <section
              className="relative card p-3 mt-4 stake-overview__table"
              aria-labelledby="stake-overview-assets-heading">
              <h2
                id="stake-overview-assets-heading"
                className="visually-hidden">
                {t("supportedStakingAssets")}
              </h2>
              <Suspense fallback={<StakeOverviewFallback />}>
                <StakeOverviewTable />
              </Suspense>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

const StakeOverviewTable: FC = () => {
  const { t } = useTranslation();
  const { sortBy, sortDir, toggleSort } = useStakeOverviewSort();
  const analyticsWindow = useStakeAnalyticsWindow();
  const data = useLazyLoadQuery<StakeOverviewQuery>(query, analyticsWindow, {
    fetchPolicy: "store-and-network",
  });
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<StakeOverviewAccountFragment$key>(
    accountFragment,
    accountData ?? null
  );
  const stakingAccounts = account?.stakingV2 ?? EMPTY_STAKING_ACCOUNTS;
  const balances = usePreloadedBalances();

  const pools = useMemo(() => getOverviewStakePools(data), [data]);
  const stakingAccountByPoolId = useMemo(
    () => createStakeAccountByPoolId(stakingAccounts),
    [stakingAccounts]
  );
  const sortedPools = useMemo(
    () => sortStakePools(pools, stakingAccountByPoolId, sortBy, sortDir),
    [pools, sortBy, sortDir, stakingAccountByPoolId]
  );
  const { pageRows: visiblePools, pagination } = useTablePagination(
    sortedPools,
    { resetKey: `${sortBy}:${sortDir}` }
  );

  return (
    <>
      <TableFrame
        columns={STAKE_OVERVIEW_COLUMNS}
        stickyFirstColumn
        tableClassName="table table--fixed table--big condensed mb-0">
        <StakeOverviewHead
          sortBy={sortBy}
          sortDir={sortDir}
          toggleSort={toggleSort}
        />
        <tbody>
          {visiblePools.map((pool) => (
            <StakeOverviewRow
              key={pool.id}
              pool={pool}
              account={stakingAccountByPoolId.get(pool.id)}
              hasStakeableBalance={hasStakeableWalletBalance(balances, pool)}
            />
          ))}
          {sortedPools.length === 0 && (
            <tr>
              <td colSpan={STAKE_OVERVIEW_COLUMNS.length}>
                <span className="color-grey fs-12">{t("empty")}</span>
              </td>
            </tr>
          )}
        </tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  );
};

const StakeOverviewRow: FC<{
  pool: StakeOverviewPool;
  account?: StakeOverviewStakingAccount;
  hasStakeableBalance: boolean;
}> = ({ pool, account, hasStakeableBalance }) => {
  const { toRoot } = useLocale();
  const { t } = useTranslation();
  const symbol = pool.bondAsset.metadata.symbol;
  const url = toRoot(stakeRoute(symbol));
  const totalStaked = totalPoolStake(pool);
  const accountStaked = totalAccountStake(account);
  const accountValueUsd = account?.valueUsd ?? 0n;
  const decimals = pool.bondAsset.metadata.decimals;
  const autoCompounding = autoCompoundingPercentage(pool.stakingStatus);
  const hasStakePosition = accountStaked > 0n;
  const hasActiveStakeAction = hasStakePosition || hasStakeableBalance;
  const actionLabel = hasStakePosition
    ? t("managePosition")
    : t("createPosition");
  const actionShortLabel = hasStakePosition
    ? t("managePositionShort")
    : t("createPositionShort");

  return (
    <tr>
      <Subscription id={pool.id} subscription={subscription} />
      <td className="stake-overview__logo-cell" aria-hidden="true">
        <IconDenom denom={symbol} className="w-4 block" />
      </td>
      <td className="stake-overview__token-cell">
        <Link to={url} className="stake-overview__token no-underline">
          <h3 className="condensed fs-22 lh-22 fs-lg-28 lh-lg-28 fw-400 mb-0 ml-1 nowrap color-white">
            {symbol}
          </h3>
        </Link>
      </td>
      <td>
        {pool.stakingStatus ? (
          <div className="flex dir-c">
            <Fiat
              amount={BigInt(pool.stakingStatus.valueUsd)}
              symbol="$"
              decimals={8}
              className="fs-16 fs-md-18 color-white"
              clip
            />
            <Decimal
              amount={totalStaked}
              decimals={decimals}
              round={4}
              symbol={symbol}
              className="fs-12 fs-md-14 condensed color-grey mt-0.5"
              clip
            />
          </div>
        ) : (
          <EmptyValue />
        )}
      </td>
      <td className="stake-overview__auto-compound-cell">
        <AutoCompoundingIndicator percentage={autoCompounding} />
      </td>
      <td className="text-center">
        <YearlyRewardsPerToken pool={pool} />
      </td>
      <td className={`text-center ${classApr(pool.stakingSummary.apr)}`}>
        {formatApr(pool.stakingSummary.apr)}
      </td>
      <td>
        {account && accountStaked > 0n ? (
          <StakeOverviewBalance
            account={account}
            total={accountStaked}
            valueUsd={accountValueUsd}
            symbol={symbol}
            decimals={decimals}
          />
        ) : (
          <EmptyValue />
        )}
      </td>
      <td className="stake-overview__action-cell">
        <Link
          to={url}
          aria-label={actionLabel}
          className={`button button--xs button--icon-right stake-overview__action${
            hasActiveStakeAction ? "" : " button--outline button--grey"
          }`}>
          {actionShortLabel}
          <Icons.ArrowUpRight />
        </Link>
      </td>
    </tr>
  );
};

const StakeOverviewFallback: FC = () => (
  <TableFrame
    columns={STAKE_OVERVIEW_COLUMNS}
    stickyFirstColumn
    tableClassName="table table--fixed table--big condensed mb-0">
    <StakeOverviewFallbackHead />
    <tbody>
      {STAKE_OVERVIEW_LOADING_ROWS.map((row) => (
        <tr key={row}>
          <td className="stake-overview__logo-cell" aria-hidden="true">
            <div className="icon-denom skeleton br-2" />
          </td>
          <td className="stake-overview__token-cell">
            <div className="ml-1 skeleton h-1.5 w-6 br-2" />
          </td>
          <td>
            <div className="skeleton h-1.5 w-6 br-2" />
            <div className="skeleton h-1.5 w-8 br-2 mt-0.5" />
          </td>
          <td>
            <div className="skeleton br-round w-6 h-6" />
          </td>
          <td>
            <div className="skeleton h-1.5 w-6 br-2" />
          </td>
          <td>
            <div className="skeleton h-1.5 w-5 br-2" />
          </td>
          <td>
            <div className="skeleton h-1.5 w-8 br-2" />
            <div className="skeleton h-1.5 w-6 br-2 mt-0.5" />
          </td>
          <td>
            <div className="skeleton h-3 w-8 br-4 ml-a" />
          </td>
        </tr>
      ))}
    </tbody>
  </TableFrame>
);

const StakeOverviewFallbackHead: FC = () => {
  const { sortBy, sortDir, toggleSort } = useStakeOverviewSort();

  return (
    <StakeOverviewHead
      sortBy={sortBy}
      sortDir={sortDir}
      toggleSort={toggleSort}
    />
  );
};

const StakeOverviewHead: FC<{
  sortBy: StakeOverviewSortBy;
  sortDir: SortDir;
  toggleSort: (value: StakeOverviewSortBy) => void;
}> = ({ sortBy, sortDir, toggleSort }) => {
  const { t } = useTranslation();

  return (
    <thead>
      <tr>
        <th aria-hidden="true" />
        <SortableTableHeader
          label={<StakeOverviewSortLabel label={t("table.token")} />}
          value="token"
          active={sortBy}
          dir={sortDir}
          onSort={toggleSort}
        />
        <SortableTableHeader
          label={
            <StakeOverviewSortLabel
              label={t("table.tvl")}
              tooltip={t("tvlTooltip")}
            />
          }
          value="tvl"
          active={sortBy}
          dir={sortDir}
          onSort={toggleSort}
        />
        <SortableTableHeader
          label={
            <StakeOverviewSortLabel
              label={t("table.autoCompounding")}
              tooltip={t("autoCompoundingTooltip")}
            />
          }
          value="autoCompounding"
          active={sortBy}
          dir={sortDir}
          onSort={toggleSort}
          align="center"
        />
        <SortableTableHeader
          label={
            <StakeOverviewSortLabel
              label={t("table.yearlyRewardsPerToken")}
              tooltip={t("yearlyRewardsPerTokenTooltip")}
            />
          }
          value="yearlyRewards"
          active={sortBy}
          dir={sortDir}
          onSort={toggleSort}
          align="center"
        />
        <SortableTableHeader
          label={
            <StakeOverviewSortLabel
              label={t("table.apr")}
              tooltip={t("aprTooltip")}
            />
          }
          value="apr"
          active={sortBy}
          dir={sortDir}
          onSort={toggleSort}
          align="center"
        />
        <SortableTableHeader
          label={<StakeOverviewSortLabel label={t("table.myBalance")} />}
          value="myBalance"
          active={sortBy}
          dir={sortDir}
          onSort={toggleSort}
        />
        <th className="text-right">
          <span className="visually-hidden">{t("table.action")}</span>
        </th>
      </tr>
    </thead>
  );
};

const StakeOverviewSortLabel: FC<{
  label: string;
  tooltip?: string;
}> = ({ label, tooltip }) =>
  tooltip ? (
    <TableTooltipLabel
      label={label}
      tooltip={`<div class="w-25">${tooltip}</div>`}
    />
  ) : (
    <span>{label}</span>
  );
const YearlyRewardsPerToken: FC<{ pool: StakeOverviewPool }> = ({ pool }) => {
  const yearlyRewards = yearlyRewardsPerToken(pool);

  if (yearlyRewards === undefined) return <EmptyValue />;

  return (
    <Fiat
      amount={yearlyRewards}
      symbol="$"
      decimals={8}
      round={4}
      className="fs-16 condensed fw-500"
      clip
    />
  );
};

const StakeOverviewBalance: FC<{
  account: StakeOverviewStakingAccount;
  total: bigint;
  valueUsd: bigint;
  symbol: string;
  decimals: number;
}> = ({ account, total, valueUsd, symbol, decimals }) => {
  const { t } = useTranslation();
  const autoCompoundingAmount = BigInt(account.liquidSize.amount);
  const yieldingAmount = BigInt(account.bonded.amount);
  const autoCompoundingUsd = stakeBalanceValueUsd(
    valueUsd,
    autoCompoundingAmount,
    total
  );
  const yieldingUsd = valueUsd - autoCompoundingUsd;
  const tooltip = balanceBreakdownTooltip({
    autoCompoundingAmount,
    autoCompoundingUsd,
    decimals,
    symbol,
    t,
    yieldingAmount,
    yieldingUsd,
  });

  return (
    <div
      className="flex dir-c stake-overview__balance"
      data-tooltip-id="global-tip"
      data-tooltip-html={tooltip}
      aria-label={tooltipText(tooltip)}>
      <Fiat
        amount={valueUsd}
        symbol="$"
        decimals={8}
        className="fs-16 condensed fw-500"
        clip
      />
      <Decimal
        amount={total}
        decimals={decimals}
        round={4}
        symbol={symbol}
        className="fs-12 fs-md-14 condensed color-grey mt-0.5"
        clip
      />
    </div>
  );
};

const AutoCompoundingIndicator: FC<{ percentage?: number }> = ({
  percentage,
}) => {
  const { t } = useTranslation();

  if (percentage === undefined) return <EmptyValue />;

  const clampedPercentage = Math.max(0, Math.min(100, percentage));
  const label = formatPercentage(clampedPercentage);

  return (
    <div
      className="stake-overview__auto-compound lending__graph-container"
      role="img"
      aria-label={t("autoCompoundingAriaLabel", { percentage: label })}>
      <Graph percentage={clampedPercentage} width={50} />
      <span className="lending__graph-container-pct" aria-hidden="true">
        {label}
      </span>
      <span className="visually-hidden">
        {t("autoCompoundingAriaLabel", { percentage: label })}
      </span>
    </div>
  );
};

const EmptyValue: FC = () => <span className="color-grey fs-12">-</span>;

const getOverviewStakePools = (
  data: StakeOverviewQuery$data
): readonly StakeOverviewPool[] =>
  data.staking.pools.filter(
    (pool) =>
      !EXCLUDED_STAKING_SYMBOLS.has(
        pool.bondAsset.metadata.symbol.toUpperCase()
      )
  );

const useStakeOverviewSort = () =>
  useTableSort<StakeOverviewSortBy>({
    initialBy: "token",
    initialDir: SortDir.Asc,
    defaultDir: defaultStakeOverviewSortDir,
  });

const defaultStakeOverviewSortDir = (value: StakeOverviewSortBy): SortDir =>
  value === "token" ? SortDir.Asc : SortDir.Desc;

const useStakeAnalyticsWindow = () => {
  const [window] = useState(createStakeAnalyticsWindow);

  return window;
};

const createStakeAnalyticsWindow = () => {
  const analyticsTo = getCurrentUtcDayStart();
  const analyticsFrom = new Date(
    analyticsTo.getTime() - STAKING_ANALYTICS_LOOKBACK_DAYS * MS_PER_DAY
  );

  return {
    analyticsFrom: analyticsFrom.toISOString(),
    analyticsPeriod: STAKING_ANALYTICS_PERIOD_DAYS,
    analyticsTo: analyticsTo.toISOString(),
  };
};

const getCurrentUtcDayStart = () => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
};

const sortStakePools = (
  pools: readonly StakeOverviewPool[],
  stakingAccountByPoolId: StakeOverviewAccountByPoolId,
  sortBy: StakeOverviewSortBy,
  sortDir: SortDir
): readonly StakeOverviewPool[] =>
  [...pools].sort((a, b) => {
    const result = compareStakePools(
      a,
      b,
      stakingAccountByPoolId,
      sortBy,
      sortDir
    );
    if (result !== 0) {
      return result;
    }

    return compareStakeSymbols(
      a.bondAsset.metadata.symbol,
      b.bondAsset.metadata.symbol
    );
  });

const compareStakePools = (
  a: StakeOverviewPool,
  b: StakeOverviewPool,
  stakingAccountByPoolId: StakeOverviewAccountByPoolId,
  sortBy: StakeOverviewSortBy,
  sortDir: SortDir
): number => {
  switch (sortBy) {
    case "token":
      return applySortDir(
        compareStakeSymbols(
          a.bondAsset.metadata.symbol,
          b.bondAsset.metadata.symbol
        ),
        sortDir
      );
    case "tvl":
      return compareNullableBigInts(
        a.stakingStatus?.valueUsd,
        b.stakingStatus?.valueUsd,
        sortDir
      );
    case "apr":
      return compareNullableBigInts(
        a.stakingSummary.apr.value,
        b.stakingSummary.apr.value,
        sortDir
      );
    case "yearlyRewards":
      return compareNullableBigInts(
        yearlyRewardsPerToken(a),
        yearlyRewardsPerToken(b),
        sortDir
      );
    case "autoCompounding":
      return compareNumbers(
        autoCompoundingPercentage(a.stakingStatus),
        autoCompoundingPercentage(b.stakingStatus),
        sortDir
      );
    case "myBalance":
      return compareBigInts(
        stakingAccountByPoolId.get(a.id)?.valueUsd ?? 0n,
        stakingAccountByPoolId.get(b.id)?.valueUsd ?? 0n,
        sortDir
      );
    default:
      return 0;
  }
};

const compareStakeSymbols = (a: string, b: string): number => {
  const rankA = stakeSymbolRank(a);
  const rankB = stakeSymbolRank(b);
  if (rankA !== rankB) return rankA - rankB;

  return a.localeCompare(b);
};

const stakeSymbolRank = (symbol: string): number =>
  STAKE_SYMBOL_ORDER.get(symbol.toUpperCase()) ?? Number.MAX_SAFE_INTEGER;

const createStakeAccountByPoolId = (
  stakingAccounts: readonly StakeOverviewStakingAccount[]
): StakeOverviewAccountByPoolId =>
  new Map(stakingAccounts.map((account) => [account.pool.id, account]));

const hasStakeableWalletBalance = (
  balances: ReturnType<typeof usePreloadedBalances>,
  pool: StakeOverviewPool
): boolean => {
  const symbols = stakeableWalletSymbols(pool.bondAsset.metadata.symbol);

  return balances.some(
    (balance) =>
      symbols.has(balance.asset.metadata.symbol.toUpperCase()) &&
      balance.balance > 0n
  );
};

const stakeableWalletSymbols = (symbol: string): ReadonlySet<string> =>
  symbol.toUpperCase() === "BRUNE"
    ? new Set(["BRUNE", "RUNE"])
    : new Set([symbol.toUpperCase()]);

const stakeBalanceValueUsd = (
  valueUsd: bigint,
  amount: bigint,
  total: bigint
): bigint => (total > 0n ? (valueUsd * amount) / total : 0n);

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const balanceBreakdownTooltip = ({
  autoCompoundingAmount,
  autoCompoundingUsd,
  decimals,
  symbol,
  t,
  yieldingAmount,
  yieldingUsd,
}: {
  autoCompoundingAmount: bigint;
  autoCompoundingUsd: bigint;
  decimals: number;
  symbol: string;
  t: (key: string) => string;
  yieldingAmount: bigint;
  yieldingUsd: bigint;
}): string => `
  <div class="stake-overview__balance-tooltip">
    <div class="stake-overview__balance-tooltip-row">
      ${escapeHtml(t("balanceBreakdown.autoCompounding"))}: ${escapeHtml(formatTooltipFiat(autoCompoundingUsd))}
      (${escapeHtml(formatTooltipToken(autoCompoundingAmount, decimals, symbol))})
    </div>
    <div class="stake-overview__balance-tooltip-row">
      ${escapeHtml(t("balanceBreakdown.yielding"))}: ${escapeHtml(formatTooltipFiat(yieldingUsd))}
      (${escapeHtml(formatTooltipToken(yieldingAmount, decimals, symbol))})
    </div>
  </div>
`;

const formatTooltipFiat = (amount: bigint): string =>
  `$ ${nFormatter(amount, 2, 8)}`;

const formatTooltipToken = (
  amount: bigint,
  decimals: number,
  symbol: string
): string => `${nFormatter(amount, 2, decimals)} ${symbol}`;

const tooltipText = (html: string): string =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const totalPoolStake = (pool: StakeOverviewPool | undefined): bigint => {
  if (!pool?.stakingStatus) return 0n;

  return (
    BigInt(pool.stakingStatus.accountBond) +
    BigInt(pool.stakingStatus.liquidBondSize)
  );
};

const totalAccountStake = (
  account: StakeOverviewStakingAccount | undefined
): bigint => {
  if (!account) return 0n;

  return BigInt(account.bonded.amount) + BigInt(account.liquidSize.amount);
};

const autoCompoundingPercentage = (
  status: StakeOverviewPool["stakingStatus"]
): number => {
  if (!status) return 0;

  const total = BigInt(status.accountBond) + BigInt(status.liquidBondSize);
  if (total === 0n) return 0;

  const basisPoints = (BigInt(status.liquidBondSize) * 10000n) / total;
  return Number(basisPoints) / 100;
};

const yearlyRewardsPerToken = (pool: StakeOverviewPool): bigint | undefined => {
  const analytics = latestStakeAnalyticsBin(pool);
  if (!analytics) return undefined;

  const dailyRevenueUsd = BigInt(analytics.totalRevenue.movingAvg);
  const totalBalanceStaked = analyticsBalanceStaked(analytics);

  if (totalBalanceStaked === 0n) return undefined;

  return (
    (dailyRevenueUsd *
      DAYS_PER_YEAR *
      10n ** BigInt(pool.bondAsset.metadata.decimals)) /
    totalBalanceStaked
  );
};

const latestStakeAnalyticsBin = (
  pool: StakeOverviewPool
): StakeOverviewAnalyticsBin | undefined => {
  const bins = (pool.analyticsBins?.edges ?? [])
    .map((edge) => edge?.node)
    .filter(
      (node): node is StakeOverviewAnalyticsBin =>
        !!node && analyticsBalanceStaked(node) > 0n && isCompletedUtcBin(node)
    )
    .sort((a, b) => new Date(a.bin).getTime() - new Date(b.bin).getTime());

  return bins[bins.length - 1];
};

const analyticsBalanceStaked = (
  analytics: StakeOverviewAnalyticsBin
): bigint => {
  const movingAverage = BigInt(analytics.totalBalanceStaked.movingAvg);

  return movingAverage > 0n
    ? movingAverage
    : BigInt(analytics.totalBalanceStaked.value);
};

const isCompletedUtcBin = (analytics: StakeOverviewAnalyticsBin): boolean =>
  new Date(analytics.bin).getTime() < getCurrentUtcDayStart().getTime();

const formatPercentage = (percentage: number): string =>
  `${percentage.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
